import React, { useState, useRef, useEffect } from "react";
import styles from "./AddNewLeadModal.module.scss";
import classNames from "classnames/bind";
import BaseInputV2 from "~/components/common/base-input-v2/BaseInputV2";
import BaseAddressInput from "~/components/common/base-address-input/BaseAddressInput";
import { googlePlacesApiKey } from "~/utils/constants/env";
import crmApi from "~/apis/crm";
import { notify, handleApiError } from "~/utils/helper";
import inputV2Styles from "~/components/common/base-input-v2/BaseInputV2.module.scss";
const cx = classNames.bind(styles);
const inputV2Cx = classNames.bind(inputV2Styles);

interface AddNewLeadModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSubmit: (values: any) => void;
}

const countryCallingCodes = [
  "+1",
  "+7",
  "+20",
  "+27",
  "+30",
  "+31",
  "+32",
  "+33",
  "+34",
  "+36",
  "+39",
  "+40",
  "+41",
  "+43",
  "+44",
  "+45",
  "+46",
  "+47",
  "+48",
  "+49",
  "+51",
  "+52",
  "+53",
  "+54",
  "+55",
  "+56",
  "+57",
  "+58",
  "+60",
  "+61",
  "+62",
  "+63",
  "+64",
  "+65",
  "+66",
  "+81",
  "+82",
  "+84",
  "+86",
  "+90",
  "+91",
  "+92",
  "+93",
  "+94",
  "+95",
  "+98",
  "+211",
  "+212",
  "+213",
  "+216",
  "+218",
  "+220",
  "+221",
  "+222",
  "+223",
  "+224",
  "+225",
  "+226",
  "+227",
  "+228",
  "+229",
  "+230",
  "+231",
  "+232",
  "+233",
  "+234",
  "+235",
  "+236",
  "+237",
  "+238",
  "+239",
  "+240",
  "+241",
  "+242",
  "+243",
  "+244",
  "+245",
  "+246",
  "+248",
  "+249",
  "+250",
  "+251",
  "+252",
  "+253",
  "+254",
  "+255",
  "+256",
  "+257",
  "+258",
  "+260",
  "+261",
  "+262",
  "+263",
  "+264",
  "+265",
  "+266",
  "+267",
  "+268",
  "+269",
  "+290",
  "+291",
  "+297",
  "+298",
  "+299",
  "+350",
  "+351",
  "+352",
  "+353",
  "+354",
  "+355",
  "+356",
  "+357",
  "+358",
  "+359",
  "+370",
  "+371",
  "+372",
  "+373",
  "+374",
  "+375",
  "+376",
  "+377",
  "+378",
  "+379",
  "+380",
  "+381",
  "+382",
  "+383",
  "+385",
  "+386",
  "+387",
  "+389",
  "+420",
  "+421",
  "+423",
  "+500",
  "+501",
  "+502",
  "+503",
  "+504",
  "+505",
  "+506",
  "+507",
  "+508",
  "+509",
  "+590",
  "+591",
  "+592",
  "+593",
  "+594",
  "+595",
  "+596",
  "+597",
  "+598",
  "+599",
  "+670",
  "+672",
  "+673",
  "+674",
  "+675",
  "+676",
  "+677",
  "+678",
  "+679",
  "+680",
  "+681",
  "+682",
  "+683",
  "+685",
  "+686",
  "+687",
  "+688",
  "+689",
  "+690",
  "+691",
  "+692",
  "+850",
  "+852",
  "+853",
  "+855",
  "+856",
  "+870",
  "+871",
  "+872",
  "+873",
  "+874",
  "+875",
  "+876",
  "+877",
  "+878",
  "+879",
  "+880",
  "+881",
  "+882",
  "+883",
  "+884",
  "+885",
  "+886",
  "+888",
  "+960",
  "+961",
  "+962",
  "+963",
  "+964",
  "+965",
  "+966",
  "+967",
  "+968",
  "+969",
  "+970",
  "+971",
  "+972",
  "+973",
  "+974",
  "+975",
  "+976",
  "+977",
  "+979",
  "+991",
  "+992",
  "+993",
  "+994",
  "+995",
  "+996",
  "+997",
  "+998",
];

const AddNewLeadModal: React.FC<AddNewLeadModalProps> = ({
  isOpen,
  onCancel,
  onSubmit,
}) => {
  const [countryCallingCode, setCountryCallingCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    primaryPhone: "",
    primaryEmail: "",
    addressLine1: "",
    latitude: 0,
    longitude: 0,
  });
  const [error, setError] = useState({
    name: "",
    countryCallingCode: "",
    phoneNumber: "",
    primaryEmail: "",
    addressLine1: "",
  });
  const [selectedSuggestion, setSelectedSuggestion] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPhoneDropdown, setShowPhoneDropdown] = useState(false);
  const phoneDropdownRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close phone dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        phoneDropdownRef.current &&
        !phoneDropdownRef.current.contains(event.target as Node)
      ) {
        setShowPhoneDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle address suggestion selection
  const handleAddressSuggestionSelect = async (suggestion: any) => {
    setSelectedSuggestion(suggestion);
    const addressText = suggestion?.placePrediction?.text?.text || "";
    setFormData((prev) => ({
      ...prev,
      addressLine1: addressText,
      latitude: 0,
      longitude: 0,
    }));

    // Fetch latitude/longitude from place details
    const placeId = suggestion?.placePrediction?.placeId;
    if (placeId) {
      try {
        const res = await fetch(
          `https://places.googleapis.com/v1/places/${placeId}`,
          {
            method: "GET",
            headers: {
              "X-Goog-Api-Key":
                googlePlacesApiKey || "AIzaSyBFvJr6l0XfUv2axQAvHaHnTzwzwlKJ1oo",
              "X-Goog-FieldMask": "location,displayName,formattedAddress",
            },
          }
        );
        const data = await res.json();
        const location = data?.location;
        if (location) {
          setFormData((prev) => ({
            ...prev,
            latitude: location.latitude,
            longitude: location.longitude,
            addressLine1: addressText,
          }));
        }
      } catch (err) {
        console.error("Error fetching place details:", err);
      }
    }
  };

  if (!isOpen) return null;

  const handleInputChange = (field: keyof typeof error, value: string) => {
    // Clear selected suggestion and coordinates if address is manually changed
    if (field === "addressLine1") {
      setSelectedSuggestion(null);
      setFormData((prev) => ({
        ...prev,
        [field]: value,
        latitude: 0,
        longitude: 0,
      }));
    } else {
      setFormData({ ...formData, [field]: value });
    }

    let errorMsg = "";
    if (field === "name" && !value) errorMsg = "Full name is required";
    if (field === "primaryEmail") {
      if (!value) errorMsg = "Email is required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
        errorMsg = "Email is invalid";
    }
    if (field === "addressLine1" && !value) errorMsg = "Address is required";

    setError((prev) => ({ ...prev, [field]: errorMsg }));
  };

  const handlePhoneNumberChange = (value: string) => {
    let cleaned = value.replace(/\D/g, "");
    if (cleaned.startsWith("0")) {
      cleaned = cleaned.slice(1);
    }
    setPhoneNumber(value);
    setFormData({
      ...formData,
      primaryPhone: countryCallingCode + cleaned,
    });

    // Validate phone
    let phoneError = "";
    if (!value) phoneError = "Phone number is required";
    else if (!/^\d+$/.test(value)) phoneError = "Phone number must be numeric";
    setError((prev) => ({ ...prev, phoneNumber: phoneError }));
  };

  const handleCountryCodeChange = (value: string) => {
    let cleaned = phoneNumber.replace(/\D/g, "");
    if (cleaned.startsWith("0")) {
      cleaned = cleaned.slice(1);
    }
    setCountryCallingCode(value);
    setFormData({
      ...formData,
      primaryPhone: value + cleaned,
    });

    // Validate country code
    let codeError = "";
    if (!value) codeError = "Country code is required";
    setError((prev) => ({ ...prev, countryCallingCode: codeError }));
  };


  const validateForm = () => {
    const newError: typeof error = {
      name: "",
      countryCallingCode: "",
      phoneNumber: "",
      primaryEmail: "",
      addressLine1: "",
    };

    if (!formData.name) newError.name = "Full name is required";
    if (!countryCallingCode)
      newError.countryCallingCode = "Country code is required";
    if (!phoneNumber) newError.phoneNumber = "Phone number is required";
    if (!formData.primaryEmail) {
      newError.primaryEmail = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.primaryEmail)) {
      newError.primaryEmail = "Email is invalid";
    }
    if (!formData.addressLine1) newError.addressLine1 = "Address is required";

    setError(newError);

    return Object.values(newError).every((v) => v === "");
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await crmApi.createCustomer({
        name: formData.name,
        email: formData.primaryEmail,
        phoneNumber: formData.primaryPhone,
        address: formData.addressLine1,
        latitude: formData.latitude,
        longitude: formData.longitude,
      });
      
      notify("success", "Customer created successfully!");
      onCancel();
      // Call onSubmit callback if provided (for backward compatibility)
      if (onSubmit) {
        onSubmit(formData);
      }
    } catch (error) {
      handleApiError(error, undefined, true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFormContent = () => {
    return (
      <>
        <BaseInputV2
          type="input"
          label="Full name"
          placeholder="Full name"
          value={formData.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
          errorMessage={error.name}
        />
        <div className={inputV2Cx("base-input-v2-container")}>
          <h3 className={inputV2Cx("input-label")}>Phone Number</h3>
          <div
            className={cx("phone-input-wrapper", {
              error: error.countryCallingCode || error.phoneNumber,
            })}
            ref={phoneDropdownRef}
          >
            <div
              className={cx("phone-type-selector", {
                open: showPhoneDropdown,
              })}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowPhoneDropdown((prev) => !prev);
              }}
            >
              <span className={cx("phone-type-value")}>
                {countryCallingCode || "Mobile"}
              </span>
              <svg
                width="7"
                height="4"
                viewBox="0 0 7 4"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={cx("phone-type-arrow", { rotated: showPhoneDropdown })}
              >
                <path
                  d="M2.807 3.373L0.129999 0.696C0.0933325 0.659333 0.062333 0.616334 0.0369997 0.567C0.012333 0.518334 0 0.466 0 0.41C0 0.298 0.0366663 0.201667 0.11 0.121C0.184666 0.0403336 0.282666 0 0.403999 0H6.081C6.20167 0 6.299 0.0413329 6.373 0.124C6.447 0.206666 6.484 0.302666 6.484 0.412C6.484 0.44 6.44067 0.534667 6.354 0.696L3.677 3.373C3.615 3.435 3.54833 3.48267 3.477 3.516C3.40567 3.54933 3.32733 3.566 3.242 3.566C3.15667 3.566 3.07833 3.54933 3.007 3.516C2.93567 3.48267 2.869 3.435 2.807 3.373Z"
                  fill="white"
                />
              </svg>
            </div>
            {showPhoneDropdown && (
              <div className={cx("phone-dropdown-list")}>
                {countryCallingCodes.map((code, index) => (
                  <div
                    key={index}
                    className={cx("phone-dropdown-item", {
                      selected: code === countryCallingCode,
                    })}
                    onClick={() => {
                      handleCountryCodeChange(code);
                      setShowPhoneDropdown(false);
                    }}
                  >
                    {code}
                  </div>
                ))}
              </div>
            )}
            <input
              type="text"
              className={inputV2Cx("input-field", {
                error: error.countryCallingCode || error.phoneNumber,
              })}
              value={phoneNumber}
              onChange={(e) => handlePhoneNumberChange(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="Phone Number"
            />
          </div>
          {(error.countryCallingCode || error.phoneNumber) && (
            <div className={inputV2Cx("error-message")}>
              {error.countryCallingCode || error.phoneNumber}
            </div>
          )}
        </div>
        <BaseInputV2
          type="input"
          label="Email ID"
          placeholder="Email ID"
          value={formData.primaryEmail}
          onChange={(e) => handleInputChange("primaryEmail", e.target.value)}
          errorMessage={error.primaryEmail}
        />
        <BaseAddressInput
          value={formData.addressLine1}
          onChange={(value) => handleInputChange("addressLine1", value)}
          onSuggestionSelect={handleAddressSuggestionSelect}
          placeholder="Address"
          label="Address"
          errorMessage={error.addressLine1}
        />
      </>
    );
  };

  const renderActionButtons = () => {
    return (
      <div className={cx("action-buttons")}>
        <button className={cx("cancel-button")} onClick={onCancel}>
          Cancel
        </button>
        <button
          className={cx("select-address-button")}
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          <p className={cx("select-address-text")}>
            {isSubmitting ? "Saving..." : "Save"}
          </p>
        </button>
      </div>
    );
  };

  return (
    <div className={cx("overlay")} onClick={onCancel}>
      <div
        className={cx("add-new-lead-container")}
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h3 className={cx("modal-title")}>Add New Lead</h3>
          <div className={cx("form-container")}>{renderFormContent()}</div>
        </div>
        {renderActionButtons()}
      </div>
    </div>
  );
};

export default AddNewLeadModal;